import {deserializeV2, fetchAndDeserialize, serialize} from '../../util/api-adapter';
import {FetchMethodEnum, fetchNoContent, fetchX} from '../../util/fetch';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../api/api-const';

import {
    BrowserNotificationsType,
    EmailNotificationsType,
    telegramAccountsSchema,
    TelegramAccountsType,
    telegramAnalyticsSettingsSchema,
    TelegramAnalyticsSettingsType,
    telegramLinkSchema,
    TelegramLinkType,
    TelegramNotificationsType,
    UpdateTelegramAnalyticsSettingsType,
    usedeskCatalogResponseSchema,
    UsedeskCatalogResponseType,
} from './notifications-settings-type';

export const telegramNotificationsUrl = '/v2/notifier/api/v1/tg_bot';

export function fetchEmailNotifications(): Promise<EmailNotificationsType> {
    return fetchX<EmailNotificationsType>(rootApiUrl + '/v1/user/notification_settings/');
}

export function updateEmailNotifications(settings: EmailNotificationsType): Promise<EmailNotificationsType> {
    return fetchX<EmailNotificationsType>(rootApiUrl + '/v1/user/notification_settings/', {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(settings),
    });
}

export function fetchBrowserNotifications(): Promise<BrowserNotificationsType> {
    return fetchX<BrowserNotificationsType>(rootApiUrl + '/v1/user/notification_settings/push_alert_settings/');
}

export function updateBrowserNotifications(settings: BrowserNotificationsType): Promise<BrowserNotificationsType> {
    return fetchX<BrowserNotificationsType>(rootApiUrl + '/v1/user/notification_settings/push_alert_settings/', {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(settings),
    });
}

export function fetchTelegramNotifications(): Promise<TelegramNotificationsType> {
    return fetchX<TelegramNotificationsType>(rootApiUrl + '/v1/user/notification_settings/messenger_settings/');
}

export function updateTelegramNotifications(settings: TelegramNotificationsType): Promise<TelegramNotificationsType> {
    return fetchX<TelegramNotificationsType>(rootApiUrl + '/v1/user/notification_settings/messenger_settings/', {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(settings),
    });
}

export async function fetchUsedeskCatalogList(): Promise<UsedeskCatalogResponseType> {
    const url = '/cp/notification_settings/usedesk/catalogs/';
    const response = await fetchX<UsedeskCatalogResponseType>(rootApiUrl + url);

    return deserializeV2<UsedeskCatalogResponseType>(url, usedeskCatalogResponseSchema, response);
}

export function fetchInvitationLink(): Promise<TelegramLinkType> {
    return fetchAndDeserialize(`${telegramNotificationsUrl}/invitation_link/`, telegramLinkSchema, {
        fetchOptions: {
            method: FetchMethodEnum.get,
            headers: {...mainApiHeaders, ...getCsrfHeaders()},
        },
    });
}

export function fetchTelegramDirectLink(): Promise<TelegramLinkType> {
    return fetchAndDeserialize(`${telegramNotificationsUrl}/direct_link/`, telegramLinkSchema, {
        fetchOptions: {
            method: FetchMethodEnum.get,
            headers: {...mainApiHeaders, ...getCsrfHeaders()},
        },
    });
}

export function fetchTelegramAccounts(): Promise<TelegramAccountsType> {
    return fetchAndDeserialize(`${telegramNotificationsUrl}/user_tg_accounts/`, telegramAccountsSchema, {
        fetchOptions: {
            method: FetchMethodEnum.get,
            headers: {...mainApiHeaders, ...getCsrfHeaders()},
            skipCache: true,
        },
    });
}

export function fetchTelegramAnalyticsSettings(): Promise<TelegramAnalyticsSettingsType> {
    return fetchAndDeserialize(`${telegramNotificationsUrl}/analytics-settings/`, telegramAnalyticsSettingsSchema, {
        fetchOptions: {
            method: FetchMethodEnum.get,
            headers: {...mainApiHeaders, ...getCsrfHeaders()},
        },
    });
}

export function getUpdateTelegramAnalyticsSettingsUrl(settingsId: string): string {
    return `${telegramNotificationsUrl}/analytics-settings/${settingsId}/`;
}

export function updateTelegramAnalyticsSettings(
    settingsId: string,
    values: UpdateTelegramAnalyticsSettingsType
): Promise<TelegramAnalyticsSettingsType> {
    return fetchAndDeserialize(getUpdateTelegramAnalyticsSettingsUrl(settingsId), telegramAnalyticsSettingsSchema, {
        fetchOptions: {
            method: FetchMethodEnum.patch,
            headers: {...mainApiHeaders, ...getCsrfHeaders()},
            body: JSON.stringify(serialize(values)),
        },
    });
}

export function disconnectTelegramAccount(id: string): Promise<void> {
    return fetchNoContent(`${rootApiUrl}${telegramNotificationsUrl}/deactivate_tg_account/${id}`, {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}
